import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { contentAdminService } from '../services/contentAdminService';
import type { 
  ContentSnapshot,
  ValidationError,
} from '../services/contentAdminService';
import type { 
  ContentVersion,
  ActiveContentInfo,
} from '../services/contentSnapshotService';

interface ContentAdminState {
  // Data
  versions: ContentVersion[];
  currentVersion: ContentVersion | null;
  currentContent: ContentSnapshot | null;
  activeContentInfo: ActiveContentInfo | null;
  
  // UI State
  isLoading: boolean;
  isSaving: boolean;
  isValidating: boolean;
  isDirty: boolean;
  error: string | null;
  validationErrors: ValidationError[];
  
  // Permissions
  hasAdminAccess: boolean | null;
  
  // Metadata
  lastFetch: number | null;
  lastSave: number | null;
  autoSaveEnabled: boolean;

  // === VERSION MANAGEMENT ACTIONS ===
  
  fetchVersions: () => Promise<void>;
  fetchVersion: (versionId: string) => Promise<void>;
  createVersion: (description: string, createdBy: string) => Promise<ContentVersion>;
  activateVersion: (versionId: string) => Promise<void>;
  deleteVersion: (versionId: string) => Promise<void>;
  duplicateVersion: (versionId: string, newDescription: string, createdBy: string) => Promise<ContentVersion>;
  
  // === CONTENT MANAGEMENT ACTIONS ===
  
  fetchVersionContent: (versionId: string) => Promise<void>;
  updateVersionContent: (versionId: string, content: ContentSnapshot) => Promise<void>;
  
  // === VALIDATION ACTIONS ===
  
  validateContent: (versionId: string) => Promise<void>;
  previewContent: (versionId: string) => Promise<ContentSnapshot | null>;
  
  // === UI ACTIONS ===
  
  setCurrentVersion: (version: ContentVersion | null) => void;
  setCurrentContent: (content: ContentSnapshot | null) => void;
  setDirty: (isDirty: boolean) => void;
  clearError: () => void;
  clearValidationErrors: () => void;
  
  // === ADMIN ACCESS ===
  
  checkAdminAccess: () => Promise<void>;
  
  // === UTILITY ACTIONS ===
  
  reset: () => void;
  getVersionStats: () => Promise<{ total: number; active: number; drafts: number; lastUpdated: string }>;
}

export const useContentAdminStore = create<ContentAdminState>()(
  devtools(
    (set, get) => ({
      // Initial state
      versions: [],
      currentVersion: null,
      currentContent: null,
      activeContentInfo: null,
      isLoading: false,
      isSaving: false,
      isValidating: false,
      isDirty: false,
      error: null,
      validationErrors: [],
      hasAdminAccess: null,
      lastFetch: null,
      lastSave: null,
      autoSaveEnabled: true,

      // === VERSION MANAGEMENT ACTIONS ===

      fetchVersions: async () => {
        set({ isLoading: true, error: null });
        
        try {
          const versions = await contentAdminService.listVersions();
          set({ 
            versions, 
            isLoading: false, 
            lastFetch: Date.now(),
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch versions';
          console.error('Error fetching versions:', error);
          set({ 
            error: errorMessage, 
            isLoading: false,
          });
        }
      },

      fetchVersion: async (versionId: string) => {
        set({ isLoading: true, error: null });
        
        try {
          const version = await contentAdminService.getVersion(versionId);
          set({ 
            currentVersion: version,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch version';
          console.error('Error fetching version:', error);
          set({ 
            error: errorMessage, 
            isLoading: false,
          });
        }
      },

      createVersion: async (description: string, createdBy: string) => {
        set({ isSaving: true, error: null });
        
        try {
          const newVersion = await contentAdminService.createVersion({
            description,
            created_by: createdBy,
          });
          
          // Atualiza lista de versões
          const currentVersions = get().versions;
          set({ 
            versions: [newVersion, ...currentVersions],
            currentVersion: newVersion,
            isSaving: false,
            error: null,
          });
          
          return newVersion;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to create version';
          console.error('Error creating version:', error);
          set({ 
            error: errorMessage, 
            isSaving: false,
          });
          throw error;
        }
      },

      activateVersion: async (versionId: string) => {
        set({ isSaving: true, error: null });
        
        try {
          await contentAdminService.activateVersion(versionId);
          
          // Atualiza estado das versões (desativa todas, ativa a selecionada)
          const updatedVersions = get().versions.map(v => ({
            ...v,
            is_active: v.id === versionId,
          }));
          
          set({ 
            versions: updatedVersions,
            isSaving: false,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to activate version';
          console.error('Error activating version:', error);
          set({ 
            error: errorMessage, 
            isSaving: false,
          });
        }
      },

      deleteVersion: async (versionId: string) => {
        set({ isSaving: true, error: null });
        
        try {
          await contentAdminService.deleteVersion(versionId);
          
          // Remove da lista de versões
          const updatedVersions = get().versions.filter(v => v.id !== versionId);
          const currentVersion = get().currentVersion;
          
          set({ 
            versions: updatedVersions,
            currentVersion: currentVersion?.id === versionId ? null : currentVersion,
            currentContent: currentVersion?.id === versionId ? null : get().currentContent,
            isSaving: false,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to delete version';
          console.error('Error deleting version:', error);
          set({ 
            error: errorMessage, 
            isSaving: false,
          });
        }
      },

      duplicateVersion: async (versionId: string, newDescription: string, createdBy: string) => {
        set({ isSaving: true, error: null });
        
        try {
          const newVersion = await contentAdminService.duplicateVersion(versionId, newDescription, createdBy);
          
          // Atualiza lista de versões
          const currentVersions = get().versions;
          set({ 
            versions: [newVersion, ...currentVersions],
            currentVersion: newVersion,
            isSaving: false,
            error: null,
          });
          
          return newVersion;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to duplicate version';
          console.error('Error duplicating version:', error);
          set({ 
            error: errorMessage, 
            isSaving: false,
          });
          throw error;
        }
      },

      // === CONTENT MANAGEMENT ACTIONS ===

      fetchVersionContent: async (versionId: string) => {
        set({ isLoading: true, error: null });
        
        try {
          const content = await contentAdminService.getVersionContent(versionId);
          set({ 
            currentContent: content,
            isLoading: false,
            isDirty: false,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch version content';
          console.error('Error fetching version content:', error);
          set({ 
            error: errorMessage, 
            isLoading: false,
          });
        }
      },

      updateVersionContent: async (versionId: string, content: ContentSnapshot) => {
        set({ isSaving: true, error: null });
        
        try {
          await contentAdminService.updateVersionContent(versionId, content);
          set({ 
            currentContent: content,
            isSaving: false,
            isDirty: false,
            lastSave: Date.now(),
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to update version content';
          console.error('Error updating version content:', error);
          set({ 
            error: errorMessage, 
            isSaving: false,
          });
        }
      },

      // === VALIDATION ACTIONS ===

      validateContent: async (versionId: string) => {
        set({ isValidating: true, validationErrors: [] });
        
        try {
          const result = await contentAdminService.validateContent(versionId);
          set({ 
            validationErrors: result.errors,
            isValidating: false,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to validate content';
          console.error('Error validating content:', error);
          set({ 
            error: errorMessage,
            isValidating: false,
          });
        }
      },

      previewContent: async (versionId: string) => {
        set({ isLoading: true, error: null });
        
        try {
          const preview = await contentAdminService.previewVersion(versionId);
          set({ isLoading: false });
          return preview;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to preview content';
          console.error('Error previewing content:', error);
          set({ 
            error: errorMessage,
            isLoading: false,
          });
          return null;
        }
      },

      // === UI ACTIONS ===

      setCurrentVersion: (version: ContentVersion | null) => {
        set({ currentVersion: version });
      },

      setCurrentContent: (content: ContentSnapshot | null) => {
        set({ currentContent: content });
      },

      setDirty: (isDirty: boolean) => {
        set({ isDirty });
      },

      clearError: () => {
        set({ error: null });
      },

      clearValidationErrors: () => {
        set({ validationErrors: [] });
      },

      // === ADMIN ACCESS ===

      checkAdminAccess: async () => {
        try {
          const hasAccess = await contentAdminService.checkAdminAccess();
          set({ hasAdminAccess: hasAccess, error: null });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to check admin access';
          console.error('Error checking admin access:', error);
          set({ 
            hasAdminAccess: false,
            error: errorMessage,
          });
        }
      },

      // === UTILITY ACTIONS ===

      reset: () => {
        set({
          versions: [],
          currentVersion: null,
          currentContent: null,
          activeContentInfo: null,
          isLoading: false,
          isSaving: false,
          isValidating: false,
          isDirty: false,
          error: null,
          validationErrors: [],
          lastFetch: null,
          lastSave: null,
        });
      },

      getVersionStats: async () => {
        try {
          return await contentAdminService.getVersionStats();
        } catch (error) {
          console.error('Error getting version stats:', error);
          throw error;
        }
      },
    }),
    {
      name: 'content-admin-store',
      partialize: (state) => ({
        // Persiste apenas configurações básicas
        autoSaveEnabled: state.autoSaveEnabled,
        hasAdminAccess: state.hasAdminAccess,
      }),
    }
  )
);

// Hooks especializados para diferentes casos de uso
export const useVersions = () => {
  const store = useContentAdminStore();
  return {
    versions: store.versions,
    isLoading: store.isLoading,
    error: store.error,
    fetchVersions: store.fetchVersions,
    createVersion: store.createVersion,
    activateVersion: store.activateVersion,
    deleteVersion: store.deleteVersion,
    duplicateVersion: store.duplicateVersion,
  };
};

export const useCurrentVersion = () => {
  const store = useContentAdminStore();
  return {
    currentVersion: store.currentVersion,
    currentContent: store.currentContent,
    isDirty: store.isDirty,
    isSaving: store.isSaving,
    fetchVersion: store.fetchVersion,
    fetchVersionContent: store.fetchVersionContent,
    updateVersionContent: store.updateVersionContent,
    setCurrentVersion: store.setCurrentVersion,
    setCurrentContent: store.setCurrentContent,
    setDirty: store.setDirty,
  };
};

export const useContentValidation = () => {
  const store = useContentAdminStore();
  return {
    validationErrors: store.validationErrors,
    isValidating: store.isValidating,
    validateContent: store.validateContent,
    previewContent: store.previewContent,
    clearValidationErrors: store.clearValidationErrors,
  };
};

export const useAdminAccess = () => {
  const store = useContentAdminStore();
  return {
    hasAdminAccess: store.hasAdminAccess,
    checkAdminAccess: store.checkAdminAccess,
    error: store.error,
    clearError: store.clearError,
  };
};